import type { CustomExperimentSpec } from '@learnforge/demo-render/harness';
import { defineExperiment } from '@learnforge/demo-render/harness';
import { safeStorage, createToggleRow } from '@learnforge/demo-kit';
import { createActionBar, type ActionBarButton } from '../action-bar';
import { regroupSteps, type Column } from '../arith/base';
import { createSvgEl } from '@learnforge/demo-render';
import type { ArithContext, ArithKey } from '../types';

export interface RegroupAnimation {
  fromPlace: number;
  toPlace: number;
}

// Converts a column-pair addition into the ordered list of carry animations
// (place → place+1). Derived from the already-tested regroupSteps: each step
// that pulls a column back below the base is one carry.
export function animateAdditionPlan(a: Column, b: Column, base: number): RegroupAnimation[] {
  const steps = regroupSteps(a, b, base);
  const plan: RegroupAnimation[] = [];
  for (let i = 1; i < steps.length; i += 1) {
    const prev = steps[i - 1]?.columns ?? [];
    const cur = steps[i]?.columns ?? [];
    for (let place = 0; place < cur.length; place += 1) {
      if ((prev[place] ?? 0) >= base && (cur[place] ?? 0) < base) {
        plan.push({ fromPlace: place, toPlace: place + 1 });
        break;
      }
    }
  }
  return plan;
}

const U = 16; // unit-square edge
const GROUP = 10 * U; // a "hundred-group" is a 10×10 block
const GROUP_GAP = 18; // gap BETWEEN 10×10 groups — equal horizontally and vertically
const GROUP_PITCH = GROUP + GROUP_GAP;
const GROUPS_PER_SUBCOL = 5; // a sub-column stacks up to 5 groups (50 tall), then wraps
const COLUMN_SPACING = 64; // larger gap between the 100s / 10s / 1s places
const STAGE_W = 760;
const STAGE_H = 420;
const BASE_Y = 330; // groups grow upward from this baseline
const TOP_MARGIN = 16;
const MARGIN_X = 20;
const LABEL_DY = 22; // column label sits this far (in screen px) below the baseline

const REGROUP_ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20V7"/><path d="M6 12l6-6 6 6"/><path d="M5 21h14"/></svg>';
const RESET_ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 1 0 3-6.7"/><path d="M3 4v4h4"/></svg>';

const ADVANCED_KEY = 'arithmetic.place-value-blocks.advanced';

interface State {
  hundreds: number;
  tens: number;
  units: number;
  advanced: boolean;
}

function buildSpec(): CustomExperimentSpec<State, ArithKey, ArithContext> {
  return {
    labelKey: 'experiment.place-value-blocks.label' as ArithKey,
    demoId: 'arithmetic',
    advancedKey: 'place-value-blocks',

    initialState(ctx): State {
      return {
        hundreds: 2,
        tens: 3,
        units: 4,
        advanced: ctx.advanced,
      };
    },

    createRender(ctx, host, controls) {
      const svg = createSvgEl('svg', {
        viewBox: `0 0 ${STAGE_W} ${STAGE_H}`,
        class: 'arith-stage',
      });
      host.appendChild(svg);

      // Blocks live in a group that zooms to fit. Labels and the value formula
      // sit OUTSIDE it so they keep their size.
      const content = createSvgEl('g', {});
      svg.appendChild(content);

      function makeLabel(text: string): SVGTextElement {
        const t = createSvgEl('text', {
          class: 'pv-col-label',
          'text-anchor': 'middle',
        });
        t.textContent = text;
        svg.appendChild(t);
        return t;
      }
      const labelH = makeLabel('100s');
      const labelT = makeLabel('10s');
      const labelU = makeLabel('1s');

      const formula = createSvgEl('text', {
        class: 'arith-readout',
        x: STAGE_W / 2,
        y: STAGE_H - 16,
        'text-anchor': 'middle',
      });
      svg.appendChild(formula);

      let manualInput: HTMLInputElement | null = null;

      // ── Panel: advanced manual value entry (built once, toggled by advanced state) ──
      function makeManualInput(): HTMLElement {
        const wrap = document.createElement('div');
        wrap.className = 'arith-adv-controls';
        const label = document.createElement('label');
        label.htmlFor = 'place-value-n';
        label.textContent = ctx.i18n.t('panel.enter-number' as ArithKey);
        const input = document.createElement('input');
        input.id = 'place-value-n';
        input.type = 'number';
        input.min = '0';
        input.max = '9999';
        input.step = '1';
        input.addEventListener('change', () => {
          const v = Math.floor(Number(input.value));
          if (Number.isFinite(v) && v >= 0 && v <= 9999) {
            controls.setState((s) => ({
              ...s,
              hundreds: Math.floor(v / 100),
              tens: Math.floor((v % 100) / 10),
              units: v % 10,
            }));
          }
        });
        manualInput = input;
        wrap.appendChild(label);
        wrap.appendChild(input);
        return wrap;
      }

      const advToggle = createToggleRow({
        i18n: ctx.i18n,
        labelKey: 'btn.advanced' as ArithKey,
        checked: ctx.advanced,
        onChange(next) {
          safeStorage.set(ADVANCED_KEY, next ? 'true' : 'false');
          controls.setState((s) => ({ ...s, advanced: next }));
        },
      });

      // Build panel once.
      function renderPanel(advanced: boolean): void {
        manualInput = null;
        ctx.panel.replaceChildren();
        if (advanced) ctx.panel.appendChild(makeManualInput());
        ctx.panel.appendChild(advToggle.element);
      }

      // ── Geometry helpers ──
      function groupBox(colX: number, g: number): { x: number; bottomY: number } {
        const subCol = Math.floor(g / GROUPS_PER_SUBCOL);
        const inSub = g % GROUPS_PER_SUBCOL;
        return { x: colX + subCol * GROUP_PITCH, bottomY: BASE_Y - inSub * GROUP_PITCH };
      }

      function gridLines(x: number, y: number, cols: number, rows: number): void {
        for (let c = 1; c < cols; c += 1) {
          content.appendChild(
            createSvgEl('line', {
              class: 'pv-grid-line',
              x1: x + c * U,
              y1: y,
              x2: x + c * U,
              y2: y + rows * U,
            }),
          );
        }
        for (let r = 1; r < rows; r += 1) {
          content.appendChild(
            createSvgEl('line', {
              class: 'pv-grid-line',
              x1: x,
              y1: y + r * U,
              x2: x + cols * U,
              y2: y + r * U,
            }),
          );
        }
      }

      let topY = BASE_Y;

      function block(
        cls: string,
        x: number,
        y: number,
        w: number,
        h: number,
        onClick: (() => void) | null,
      ): void {
        const r = createSvgEl('rect', {
          class: onClick ? `${cls} pv-clickable` : cls,
          x,
          y,
          width: w,
          height: h,
        });
        if (onClick) r.addEventListener('click', onClick);
        content.appendChild(r);
        if (y < topY) topY = y;
      }

      function subColsFor(groups: number): number {
        return Math.max(1, Math.ceil(groups / GROUPS_PER_SUBCOL));
      }
      function widthFor(groups: number): number {
        const s = subColsFor(groups);
        return s * GROUP + (s - 1) * GROUP_GAP;
      }

      function renderHundreds(colX: number, state: State): void {
        for (let g = 0; g < state.hundreds; g += 1) {
          const { x, bottomY } = groupBox(colX, g);
          const y = bottomY - GROUP;
          block('pv-hundred', x, y, GROUP, GROUP, () => {
            controls.setState((s) => ({
              ...s,
              hundreds: s.hundreds - 1,
              tens: s.tens + 10,
            }));
          });
          gridLines(x, y, 10, 10);
        }
      }

      function renderTens(colX: number, state: State): void {
        for (let i = 0; i < state.tens; i += 1) {
          const { x, bottomY } = groupBox(colX, Math.floor(i / 10));
          const inGroup = i % 10;
          const y = bottomY - (inGroup + 1) * U;
          block('pv-ten', x, y, GROUP, U, () => {
            controls.setState((s) => ({
              ...s,
              tens: s.tens - 1,
              units: s.units + 10,
            }));
          });
          gridLines(x, y, 10, 1);
        }
      }

      function renderUnits(colX: number, state: State): void {
        for (let i = 0; i < state.units; i += 1) {
          const { x, bottomY } = groupBox(colX, Math.floor(i / 100));
          const within = i % 100;
          const row = Math.floor(within / 10);
          const col = within % 10;
          block('pv-unit', x + col * U, bottomY - (row + 1) * U, U, U, null);
        }
      }

      // ── Static action bar (built once) ──
      function makeButtons(): ActionBarButton[] {
        return [
          {
            id: 'add-unit',
            labelKey: '+1',
            onClick: () => {
              controls.setState((s) => ({ ...s, units: s.units + 1 }));
            },
          },
          {
            id: 'add-rod',
            labelKey: '+10',
            onClick: () => {
              controls.setState((s) => ({ ...s, tens: s.tens + 1 }));
            },
          },
          {
            id: 'add-flat',
            labelKey: '+100',
            onClick: () => {
              controls.setState((s) => ({ ...s, hundreds: s.hundreds + 1 }));
            },
          },
          {
            id: 'regroup',
            labelKey: 'Regroup (carry tens & hundreds)',
            iconSvg: REGROUP_ICON,
            onClick: () => {
              controls.setState((s) => {
                let { hundreds, tens, units } = s;
                while (units >= 10) {
                  units -= 10;
                  tens += 1;
                }
                while (tens >= 10) {
                  tens -= 10;
                  hundreds += 1;
                }
                return { ...s, hundreds, tens, units };
              });
            },
          },
          {
            id: 'reset',
            labelKey: 'Reset',
            iconSvg: RESET_ICON,
            onClick: () => {
              controls.setState((s) => ({ ...s, hundreds: 0, tens: 0, units: 0 }));
            },
          },
        ];
      }

      const actionBar = createActionBar(ctx.actionBar, makeButtons());

      // ── Initial panel ──
      renderPanel(ctx.advanced);

      // ── RenderHandle ──
      let lastAdvanced: boolean | undefined;

      function update(state: State): void {
        // Rebuild panel when advanced flag changes.
        if (state.advanced !== lastAdvanced) {
          lastAdvanced = state.advanced;
          renderPanel(state.advanced);
        }

        // Rebuild inline SVG blocks (reattach per-block click handlers each time).
        while (content.firstChild) content.removeChild(content.firstChild);
        topY = BASE_Y;

        const groupsH = state.hundreds;
        const groupsT = Math.ceil(state.tens / 10);
        const groupsU = Math.ceil(state.units / 100);

        const hundredsX = 0;
        const tensX = hundredsX + widthFor(groupsH) + COLUMN_SPACING;
        const unitsX = tensX + widthFor(groupsT) + COLUMN_SPACING;
        const totalW = unitsX + widthFor(groupsU);

        renderHundreds(hundredsX, state);
        renderTens(tensX, state);
        renderUnits(unitsX, state);

        const contentH = Math.max(GROUP, BASE_Y - topY);
        const availW = STAGE_W - 2 * MARGIN_X;
        const availH = BASE_Y - TOP_MARGIN;
        const scale = Math.min(1, availW / totalW, availH / contentH);
        content.setAttribute(
          'transform',
          `translate(${STAGE_W / 2} ${BASE_Y}) scale(${scale}) translate(${-totalW / 2} ${-BASE_Y})`,
        );

        // Place constant-size labels at the scaled centre-x of each column.
        const screenX = (cxContent: number): number =>
          STAGE_W / 2 + (cxContent - totalW / 2) * scale;
        labelH.setAttribute('x', String(screenX(hundredsX + widthFor(groupsH) / 2)));
        labelT.setAttribute('x', String(screenX(tensX + widthFor(groupsT) / 2)));
        labelU.setAttribute('x', String(screenX(unitsX + widthFor(groupsU) / 2)));
        for (const l of [labelH, labelT, labelU]) l.setAttribute('y', String(BASE_Y + LABEL_DY));

        const v = state.hundreds * 100 + state.tens * 10 + state.units;
        formula.textContent = `${state.hundreds}·100 + ${state.tens}·10 + ${state.units} = ${v}`;

        actionBar.setButtons(makeButtons());

        // Guard: sync number-input only when it exists.
        if (manualInput) manualInput.value = String(v);
      }

      return {
        update,
        dispose() {
          actionBar.destroy();
        },
      };
    },

    applyShotState(state): State {
      return state;
    },
  };
}

export const experiment = defineExperiment(buildSpec());
export const mountPlaceValueBlocks = experiment.mount.bind(experiment);
